import { useState, useRef, useCallback, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { PhysicsCanvas } from "@/components/PhysicsCanvas";
import {
	getInitialState,
	stepPhysics,
	calculateNetForce,
	SURFACES,
} from "@/lib/physics";
import type { PhysicsState, ForceConfig } from "@/lib/physics";
import { Play, Pause, RotateCcw, Zap } from "lucide-react";

export function Lesson1Inertia() {
	const [state, setState] = useState<PhysicsState>(getInitialState());
	const [isRunning, setIsRunning] = useState(false);
	const [hasAppliedKick, setHasAppliedKick] = useState(false);
	const [selectedSurface, setSelectedSurface] = useState<string>("ice");
	const [showedNoFriction, setShowedNoFriction] = useState(false);
	const [showedFriction, setShowedFriction] = useState(false);

	const config: ForceConfig = {
		appliedForce: 0,
		mass: 5,
		frictionCoefficient: SURFACES[selectedSurface].coefficient,
		gravity: 9.8,
	};

	const { netForce, frictionForce } = calculateNetForce({
		...config,
		appliedForce:
			state.velocity !== 0 ? Math.sign(state.velocity) * 0.01 : 0,
	});

	// For inertia demo, friction acts on moving objects even without applied force
	const effectiveFriction =
		state.velocity !== 0
			? config.frictionCoefficient * config.mass * config.gravity * -Math.sign(state.velocity)
			: 0;

	const animRef = useRef<number | null>(null);
	const lastTimeRef = useRef<number>(0);

	const simulate = useCallback(
		(timestamp: number) => {
			if (lastTimeRef.current === 0) lastTimeRef.current = timestamp;
			const dt = Math.min((timestamp - lastTimeRef.current) / 1000, 0.05);
			lastTimeRef.current = timestamp;

			setState((prev) => {
				const frictionDecel =
					prev.velocity !== 0
						? -Math.sign(prev.velocity) *
							config.frictionCoefficient *
							config.gravity
						: 0;

				const newVelocity = prev.velocity + frictionDecel * dt;

				// Stop if velocity crosses zero
				const finalVelocity =
					Math.sign(newVelocity) !== Math.sign(prev.velocity) &&
					prev.velocity !== 0
						? 0
						: newVelocity;

				return {
					position: prev.position + finalVelocity * dt,
					velocity: finalVelocity,
					acceleration: frictionDecel,
					time: prev.time + dt,
				};
			});

			animRef.current = requestAnimationFrame(simulate);
		},
		[config.frictionCoefficient, config.gravity],
	);

	useEffect(() => {
		if (isRunning) {
			lastTimeRef.current = 0;
			animRef.current = requestAnimationFrame(simulate);
		} else if (animRef.current) {
			cancelAnimationFrame(animRef.current);
		}
		return () => {
			if (animRef.current) cancelAnimationFrame(animRef.current);
		};
	}, [isRunning, simulate]);

	const kick = () => {
		setState((prev) => ({
			...prev,
			velocity: prev.velocity + 8,
		}));
		setHasAppliedKick(true);
		if (!isRunning) setIsRunning(true);
		if (selectedSurface === "ice") setShowedNoFriction(true);
		if (selectedSurface === "rubber") setShowedFriction(true);
	};

	const reset = () => {
		setIsRunning(false);
		setState(getInitialState());
		lastTimeRef.current = 0;
	};

	return (
		<div className="space-y-6">
			<div className="space-y-3">
				<div className="flex items-center gap-3">
					<Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-sm px-3 py-1">
						Law 1
					</Badge>
					<h2 className="text-2xl font-bold">
						Newton's First Law: Inertia
					</h2>
				</div>
				<blockquote className="border-l-4 border-blue-500 pl-4 italic text-muted-foreground text-lg">
					"An object at rest stays at rest, and an object in motion
					stays in motion at constant velocity, unless acted upon by a
					net external force."
				</blockquote>
			</div>

			<Card className="bg-card/50 border-muted">
				<CardContent className="pt-6 space-y-4">
					<h3 className="font-semibold text-lg">
						What is Inertia?
					</h3>
					<p className="text-muted-foreground leading-relaxed">
						Inertia is the tendency of an object to resist changes in
						its state of motion. A ball sitting on a table won't
						move unless you push it. A ball rolling on a perfectly
						smooth surface will roll forever unless something stops
						it. The more massive an object, the more inertia it
						has.
					</p>
					<p className="text-muted-foreground leading-relaxed">
						In the real world, objects slow down because of
						<strong className="text-foreground"> friction</strong> --
						a force that opposes motion. Without friction (like on
						ice), objects keep moving much longer.
					</p>
				</CardContent>
			</Card>

			<Separator />

			<div className="space-y-4">
				<h3 className="font-semibold text-lg flex items-center gap-2">
					<Zap className="w-5 h-5 text-yellow-500" />
					Interactive Experiment
				</h3>
				<p className="text-sm text-muted-foreground">
					Give the block a kick, then observe how it behaves on
					different surfaces. On ice (low friction) the block slides
					much farther than on rubber (high friction).
				</p>

				<PhysicsCanvas
					state={state}
					config={config}
					netForce={0}
					frictionForce={effectiveFriction}
					surfaceColor={SURFACES[selectedSurface].color}
					showForceArrows={Math.abs(state.velocity) > 0.1}
					label="Inertia Demo"
				/>

				<div className="flex flex-wrap gap-3 items-center">
					<Button onClick={kick} className="flex items-center gap-2">
						<Zap className="w-4 h-4" /> Kick the Block
					</Button>
					<Button
						onClick={() => setIsRunning(!isRunning)}
						variant="secondary"
						className="flex items-center gap-2"
					>
						{isRunning ? (
							<>
								<Pause className="w-4 h-4" /> Pause
							</>
						) : (
							<>
								<Play className="w-4 h-4" /> Resume
							</>
						)}
					</Button>
					<Button
						onClick={reset}
						variant="outline"
						className="flex items-center gap-2"
					>
						<RotateCcw className="w-4 h-4" /> Reset
					</Button>

					<div className="flex gap-2 ml-auto">
						{Object.entries(SURFACES).map(([key, s]) => (
							<Button
								key={key}
								variant={
									selectedSurface === key
										? "default"
										: "outline"
								}
								size="sm"
								onClick={() => {
									setSelectedSurface(key);
									reset();
								}}
								className="text-xs"
							>
								{s.label}
							</Button>
						))}
					</div>
				</div>

				<div className="grid grid-cols-3 gap-3 text-center">
					<Card className="bg-muted/30 border-muted">
						<CardContent className="pt-4 pb-3">
							<p className="text-xs text-muted-foreground">
								Velocity
							</p>
							<p className="text-lg font-mono font-bold text-purple-400">
								{state.velocity.toFixed(2)} m/s
							</p>
						</CardContent>
					</Card>
					<Card className="bg-muted/30 border-muted">
						<CardContent className="pt-4 pb-3">
							<p className="text-xs text-muted-foreground">
								Distance
							</p>
							<p className="text-lg font-mono font-bold text-blue-400">
								{state.position.toFixed(2)} m
							</p>
						</CardContent>
					</Card>
					<Card className="bg-muted/30 border-muted">
						<CardContent className="pt-4 pb-3">
							<p className="text-xs text-muted-foreground">
								Friction (μ)
							</p>
							<p className="text-lg font-mono font-bold text-red-400">
								{config.frictionCoefficient.toFixed(2)}
							</p>
						</CardContent>
					</Card>
				</div>
			</div>

			{hasAppliedKick && (
				<Card className="bg-blue-500/10 border-blue-500/30">
					<CardContent className="pt-4 pb-3">
						<h4 className="font-semibold text-blue-400 mb-2">
							Key Observation
						</h4>
						<p className="text-sm text-muted-foreground">
							{selectedSurface === "ice"
								? "On ice, the block barely slows down! With almost no friction, the block's inertia keeps it moving at nearly constant velocity -- just like Newton predicted."
								: selectedSurface === "rubber"
									? "On rubber, the block stops quickly! High friction provides a large opposing force that overcomes the block's inertia."
									: `On ${SURFACES[selectedSurface].label.toLowerCase()}, notice how friction gradually decelerates the block. The friction coefficient (μ = ${config.frictionCoefficient}) determines how quickly the block's inertia is overcome.`}
						</p>
					</CardContent>
				</Card>
			)}

			<Card className="bg-card/50 border-muted">
				<CardContent className="pt-6 space-y-3">
					<h3 className="font-semibold">Key Takeaways</h3>
					<ul className="space-y-2 text-sm text-muted-foreground">
						<li className="flex items-start gap-2">
							<span className="text-blue-400 mt-0.5">1.</span>
							Objects naturally resist changes to their motion --
							this resistance is called <strong className="text-foreground">inertia</strong>.
						</li>
						<li className="flex items-start gap-2">
							<span className="text-blue-400 mt-0.5">2.</span>
							Without a <strong className="text-foreground">net external force</strong>, an object's velocity
							stays constant (including zero).
						</li>
						<li className="flex items-start gap-2">
							<span className="text-blue-400 mt-0.5">3.</span>
							<strong className="text-foreground">Friction</strong> is the most common force that causes
							everyday objects to slow down and stop.
						</li>
						<li className="flex items-start gap-2">
							<span className="text-blue-400 mt-0.5">4.</span>
							More massive objects have more inertia, making them
							harder to start or stop moving.
						</li>
					</ul>
				</CardContent>
			</Card>
		</div>
	);
}
